import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, deleteUser } from 'firebase/auth';
import { getCurrentUser, logOut, reauthenticateUser } from '../services/firebase/auth';
import { updateClimberFields, deleteClimber, removeClimberFromAllRankings } from '../services/firebase/climber';
import { GradeSystem, getGradeSystemAsString } from '../models/climbing/Grade';
import CloseButton from '../components/CloseButton';
import '../styles/Profile.css';
import '../styles/ClimberDetail.css';
import '../styles/Auth.css';

const Profile: React.FC = () => {
  const { currentUser, climberProfile, refreshClimberProfile } = useAuth();
  
  const [username, setUsername] = useState('');
  const [boulderGradeSystem, setBoulderGradeSystem] = useState<GradeSystem>(GradeSystem.FRENCH);
  const [ropeGradeSystem, setRopeGradeSystem] = useState<GradeSystem>(GradeSystem.FRENCH);
  const [age, setAge] = useState<string>('');
  const [gender, setGender] = useState<string>('M');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [showReauthModal, setShowReauthModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser && climberProfile) {
        // Set username from Auth
        if (currentUser.displayName) {
          setUsername(currentUser.displayName);
        }
        
        // Use climber profile from context instead of fetching
        try {
          // Set grade systems if they exist in the user profile
          if (climberProfile.boulderGradeSystem) {
            setBoulderGradeSystem(climberProfile.boulderGradeSystem as GradeSystem);
          }
          if (climberProfile.ropeGradeSystem) {
            setRopeGradeSystem(climberProfile.ropeGradeSystem as GradeSystem);
          }
          // Set age and gender if they exist
          if (climberProfile.age) {
            setAge(climberProfile.age.toString());
          }
          if (climberProfile.gender) {
            setGender(climberProfile.gender);
          }
          

        } catch (err: any) {
          console.error('Error fetching climber data:', err);
        }
      }
    };
    
    fetchUserData();
  }, [currentUser, climberProfile]);

  const handleLogout = async () => {
    try {
      setError('');
      await logOut();
      navigate('/login');
    } catch (err: any) {
      setError('Failed to log out: ' + (err.message || 'Unknown error'));
    }
  };

  const handleUpdateProfile = async () => {
    if (!username.trim()) {
      return setError('Username cannot be empty');
    }

    try {
      setError('');
      setLoading(true);
      const user = getCurrentUser();
      
      if (user) {
        await updateProfile(user, {
          displayName: username
        });
        
        // Update all fields in Firestore
        await updateClimberFields(user.uid, {
          username,
          boulderGradeSystem,
          ropeGradeSystem,
          age: parseInt(age) || undefined,
          gender
        });
        
        // Refresh the climber profile in context
        await refreshClimberProfile();
        
        setSuccess('Profile updated successfully!');
      } else {
        throw new Error('No user is logged in');
      }
    } catch (err: any) {
      setError('Failed to update profile: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // First step: Show reauthentication modal
  const startAccountDeletion = () => {
    setError('');
    setPassword('');
    setShowReauthModal(true);
    setShowDeleteConfirm(false);
  };

  // Second step: Attempt to reauthenticate and then delete account
  const handleReauthenticate = async () => {
    if (!password.trim()) {
      return setError('Password is required');
    }

    try {
      setError('');
      setLoading(true);
      
      // Reauthenticate with Firebase
      await reauthenticateUser(password);
      console.log('Successfully reauthenticated');
      
      // Once reauthenticated, proceed with deletion
      await deleteAccountProcess();
    } catch (err: any) {
      console.error('Failed to reauthenticate:', err);
      setError('Failed to verify your identity: ' + (err.message || 'Invalid password'));
      setLoading(false);
    }
  };

  // Third step: Actual deletion process in correct order
  const deleteAccountProcess = async () => {
    try {
      const user = getCurrentUser();
      
      if (!user) {
        throw new Error('No user is logged in');
      }
      
      const userId = user.uid;
      
      // Store user info before deletion
      const userToDelete = { ...user };
      console.log('Starting account deletion process for user:', userId);
      
      // Step 1: Remove the climber from all gym rankings first
      console.log('Step 1: Removing climber from all rankings...');
      try {
        await removeClimberFromAllRankings(userId);
        console.log('Successfully removed climber from all rankings');
      } catch (rankingsErr: any) {
        console.error('Error removing from rankings:', rankingsErr);
        // Log error but continue with process
      }
      
      // Verify rankings removal succeeded (or at least attempted)
      console.log('Rankings removal complete, proceeding to delete climber document');
      
      // Step 2: Delete the climber document and archived climbs
      console.log('Step 2: Deleting climber document...');
      try {
        await deleteClimber(userId);
        console.log('Successfully deleted climber document');
      } catch (climberErr: any) {
        console.error('Error deleting climber document:', climberErr);
        // Log error but continue with process
      }
      
      // Verify climber document deletion attempted
      console.log('Climber document deletion complete, proceeding to delete auth user');
      
      // Step 3: Delete the Firebase Auth user (should succeed now that we've reauthenticated)
      console.log('Step 3: Deleting Firebase Auth user...');
      try {
        // Make sure we're deleting the current user
        const currentUser = getCurrentUser();
        if (currentUser && currentUser.uid === userId) {
          await deleteUser(currentUser);
          console.log('Successfully deleted Firebase Auth user');
        } else {
          console.error('Current user changed during deletion process');
          throw new Error('User authentication changed during deletion process');
        }
      } catch (authErr: any) {
        console.error('Error deleting auth user:', authErr);
        throw authErr; // Rethrow auth errors as they're critical
      }
      
      // If we got here, all deletion steps completed
      console.log('Account deletion completed successfully');
      navigate('/login');
    } catch (err: any) {
      console.error('Error during account deletion:', err);
      setError('Failed to delete account: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setShowReauthModal(false);
    }
  };

  // Calculate statistics for display
  const totalClimbs = climberProfile?.getTotalClimbs() || 0;
  const gymCount = climberProfile?.getGymIds().length || 0;

  // Detect mobile
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 1024 : false;

  return (
    <div
      className="climber-detail-container"
      style={{ minHeight: isMobile ? 'calc(var(--vh, 1vh) * 100)' : 'auto' }}
    >
      <div className="climber-detail-header">
        <CloseButton returnTo="/" />
        <h1>My Profile</h1>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="climber-profile">
        <div className="climber-avatar-large">
          {currentUser?.photoURL ? (
            <img src={currentUser.photoURL} alt={username || 'User'} />
          ) : (
            <div className="avatar-placeholder-large">
              {(username || currentUser?.displayName || 'U').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <div className="climber-info-large">
          <h2>{username || currentUser?.displayName || 'User Profile'}</h2>
          <p className="climber-full-name">{currentUser?.email}</p>
          
          <div className="climber-stats-box">
            <div className="stat">
              <span className="stat-label">Total Climbs</span>
              <span className="stat-value">{totalClimbs}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Gyms</span>
              <span className="stat-value">{gymCount}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="gym-roles-section">
        <h3>Profile Settings</h3>
        
        <div className="profile-field">
          <label>Username</label>
          <div className="editable-field">
            <input 
              type="text" 
              value={username} 
              onChange={(e) => setUsername(e.target.value)} 
              onBlur={handleUpdateProfile}
              placeholder="Enter username"
            />
          </div>
        </div>
        
        <div className="profile-field">
          <label>Age</label>
          <div className="editable-field">
            <input 
              inputMode="numeric"
              pattern="[0-9]*"
              type="text" 
              value={age} 
              onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, ''))} 
              onBlur={handleUpdateProfile}
              placeholder="Enter age"
              className="form-control"
            />
          </div>
        </div>
        
        <div className="profile-field">
          <label>Gender</label>
          <div className="editable-field">
            <select
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                // Trigger update on change for select elements
                setTimeout(handleUpdateProfile, 0);
              }}
              className="form-control"
            >
              <option value="M">M</option>
              <option value="V">V</option>
            </select>
          </div>
        </div>
        
        <div className="profile-field">
          <label>Grade System Preferences</label>
          <div className="grade-system-options">
            <div className="editable-field">
              <div className="grade-label">Boulder Grades:</div>
              <select 
                value={boulderGradeSystem} 
                onChange={(e) => {
                  setBoulderGradeSystem(e.target.value as GradeSystem);
                  // Trigger update on change for select elements
                  setTimeout(handleUpdateProfile, 0);
                }}
              >
                <option value={GradeSystem.FRENCH}>French</option>
                <option value={GradeSystem.YDS}>YDS</option>
              </select>
            </div>
            
            <div className="editable-field">
              <div className="grade-label">Rope Grades:</div>
              <select 
                value={ropeGradeSystem} 
                onChange={(e) => {
                  setRopeGradeSystem(e.target.value as GradeSystem);
                  // Trigger update on change for select elements
                  setTimeout(handleUpdateProfile, 0);
                }}
              >
                <option value={GradeSystem.FRENCH}>French</option>
                <option value={GradeSystem.YDS}>YDS</option>
              </select>
            </div>
          </div>
        </div>
        
        <h3>Account Actions</h3>
        <div className="action-buttons">
          <button onClick={handleLogout} className="logout-button">
            Log Out
          </button>
          <button 
            onClick={() => setShowDeleteConfirm(true)} 
            className="delete-button"
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Are you sure?</h3>
            <p>This action is permanent and cannot be undone. All your data will be lost.</p>
            <div className="delete-confirmation-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="cancel-delete-button"
              >
                Cancel
              </button>
              <button 
                onClick={startAccountDeletion}
                className="confirm-delete-button"
                disabled={loading}
              >
                Yes, Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reauthentication Dialog */}
      {showReauthModal && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Verify Your Identity</h3>
            <p>For security reasons, please enter your password to continue:</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
              placeholder="Enter your password"
            />
            <div className="delete-confirmation-actions">
              <button 
                onClick={() => {
                  setShowReauthModal(false);
                  setPassword('');
                }}
                className="cancel-delete-button"
              >
                Cancel
              </button>
              <button 
                onClick={handleReauthenticate}
                className="confirm-delete-button"
                disabled={loading || !password.trim()}
              >
                {loading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;